import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@/i18n/I18nContext';
import { handbookContent } from '@/i18n/handbookContent';
import { useStore } from '@/state/store';
import { buildDemoProject } from '@/lib/demoProject';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  BookOpen,
  Rocket,
  Layers,
  Copy,
  Cable,
  Network,
  ShieldCheck,
  LifeBuoy,
  Info,
  AlertTriangle,
  CheckCircle2,
  Building2,
  Mail,
  Phone,
  PlayCircle,
} from 'lucide-react';

// localStorage flags. The handbook auto-opens exactly once, but only after the
// beta dialog has been acknowledged so the two dialogs never stack.
const SEEN_KEY = 'switchr-handbook-seen-v1';
const BETA_ACK_KEY = 'switchr-beta-ack-v1';

const CHAPTER_ICONS = {
  rocket: Rocket,
  pages: Layers,
  devices: Copy,
  cable: Cable,
  network: Network,
  shield: ShieldCheck,
  support: LifeBuoy,
};

const NOTE_TONES = {
  info: { Icon: Info, color: '#0B5FFF', bg: 'rgba(11,95,255,0.06)', border: 'rgba(11,95,255,0.30)' },
  warn: { Icon: AlertTriangle, color: '#B7791F', bg: 'rgba(183,121,31,0.08)', border: 'rgba(183,121,31,0.32)' },
  ok: { Icon: CheckCircle2, color: '#0F7B6C', bg: 'rgba(15,123,108,0.08)', border: 'rgba(15,123,108,0.32)' },
};

const ContactBlock = ({ contactLabel }) => (
  <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4" data-testid="handbook-contact">
    <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {contactLabel}
    </div>
    <div className="space-y-2.5 text-sm">
      <div className="flex items-center gap-2.5 font-medium text-foreground" data-testid="handbook-contact-company">
        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" /> T2 IT Systeme GmbH
      </div>
      <a
        href="mailto:info@t2.at"
        className="flex items-center gap-2.5 text-[#0B5FFF] transition-colors duration-150 hover:underline"
        data-testid="handbook-contact-email"
      >
        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" /> info@t2.at
      </a>
      <a
        href="tel:+43775522022"
        className="flex items-center gap-2.5 text-[#0B5FFF] transition-colors duration-150 hover:underline"
        data-testid="handbook-contact-phone"
      >
        <Phone className="h-4 w-4 shrink-0 text-muted-foreground" /> +43 7755 22022
      </a>
    </div>
  </div>
);

const DemoLaunch = ({ t, onLoaded }) => {
  const loadDoc = useStore((s) => s.loadDoc);
  const [loading, setLoading] = useState(false);
  const handleLoad = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const doc = await buildDemoProject();
      loadDoc(doc);
      toast.success(t('demo_loaded'));
      onLoaded?.();
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="mb-5 rounded-lg border border-[#0B5FFF]/25 bg-[rgba(11,95,255,0.05)] p-4" data-testid="handbook-demo-launcher">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">{t('demo_load')}</div>
          <p className="mt-0.5 text-sm leading-relaxed text-foreground/75">{t('demo_hint')}</p>
        </div>
        <Button
          onClick={handleLoad}
          disabled={loading}
          className="h-9 shrink-0 bg-[#0B5FFF] hover:bg-[#0a55e6]"
          data-testid="handbook-load-demo-button"
        >
          <PlayCircle className="mr-1.5 h-4 w-4" /> {t('demo_load')}
        </Button>
      </div>
      <div className="mt-3 flex gap-2.5 rounded-md border border-[#B7791F]/30 bg-[rgba(183,121,31,0.08)] p-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#B7791F]" />
        <span className="text-[13px] leading-relaxed text-foreground/80">{t('demo_note_checks')}</span>
      </div>
    </div>
  );
};

const NoteCallout = ({ tone, title, text }) => {
  const cfg = NOTE_TONES[tone] || NOTE_TONES.info;
  const { Icon } = cfg;
  return (
    <div
      className="my-4 flex gap-3 rounded-lg border p-3.5"
      style={{ background: cfg.bg, borderColor: cfg.border }}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" style={{ color: cfg.color }} />
      <div className="min-w-0">
        {title && (
          <div className="mb-0.5 text-sm font-semibold" style={{ color: cfg.color }}>
            {title}
          </div>
        )}
        <div className="text-sm leading-relaxed text-foreground/80">{text}</div>
      </div>
    </div>
  );
};

const renderBlock = (block, i, contactLabel) => {
  switch (block.type) {
    case 'h':
      return (
        <h3 key={i} className="mb-1.5 mt-6 text-sm font-semibold tracking-tight text-foreground">
          {block.text}
        </h3>
      );
    case 'p':
      return (
        <p key={i} className="mb-3 text-sm leading-relaxed text-foreground/80">
          {block.text}
        </p>
      );
    case 'list':
      return (
        <ul key={i} className="mb-3 space-y-2">
          {block.items.map((it, j) => (
            <li key={j} className="flex gap-2.5 text-sm leading-relaxed text-foreground/80">
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#0B5FFF]" aria-hidden="true" />
              {typeof it === 'string' ? (
                <span>{it}</span>
              ) : (
                <span>
                  <span className="font-medium text-foreground">{it.lead} </span>
                  {it.text}
                </span>
              )}
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol key={i} className="mb-3 space-y-3">
          {block.items.map((it, j) => (
            <li key={j} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#111418] font-mono text-[11px] font-semibold text-white tabular-nums">
                {j + 1}
              </span>
              <div className="min-w-0 pt-0.5">
                <div className="text-sm font-medium text-foreground">{it.title}</div>
                <div className="text-sm leading-relaxed text-foreground/80">{it.text}</div>
              </div>
            </li>
          ))}
        </ol>
      );
    case 'note':
      return <NoteCallout key={i} tone={block.tone} title={block.title} text={block.text} />;
    case 'contact':
      return <ContactBlock key={i} contactLabel={contactLabel} />;
    default:
      return null;
  }
};

export const HandbookDialog = ({ open, onOpenChange }) => {
  const { t, language } = useI18n();
  const content = handbookContent[language] || handbookContent.de;
  const chapters = content.chapters;
  const [activeId, setActiveId] = useState(chapters[0]?.id);
  const scrollRef = useRef(null);

  // Auto-open once on first start, but only after the beta dialog has been
  // acknowledged (so the two dialogs never stack on a brand-new visit).
  // NOTE: we intentionally do NOT write the "seen" flag here — that happens in
  // the effect below once the dialog is actually open. This keeps the logic
  // resilient to React StrictMode's double effect invocation in development.
  useEffect(() => {
    let seen = true;
    let betaAcked = false;
    try {
      seen = localStorage.getItem(SEEN_KEY) === '1';
      betaAcked = localStorage.getItem(BETA_ACK_KEY) === '1';
    } catch {
      return undefined;
    }
    if (!seen && betaAcked) {
      const id = window.setTimeout(() => onOpenChange(true), 500);
      return () => window.clearTimeout(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark as seen whenever the dialog is open (auto-open or manual open), so it
  // only ever auto-opens once.
  useEffect(() => {
    if (open) {
      try {
        localStorage.setItem(SEEN_KEY, '1');
      } catch {
        /* ignore */
      }
    }
  }, [open]);

  const active = useMemo(
    () => chapters.find((c) => c.id === activeId) || chapters[0],
    [chapters, activeId]
  );

  // Reset scroll position to top on chapter / language change.
  useEffect(() => {
    const vp = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (vp) vp.scrollTop = 0;
  }, [activeId, language]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[86vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden p-0"
        data-testid="handbook-dialog"
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#111418] text-white">
              <BookOpen className="h-4 w-4" />
            </span>
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {content.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1">
          <nav
            className="flex w-48 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-border bg-[#FBFBFA] p-2.5 md:w-60"
            data-testid="handbook-nav"
            aria-label={content.title}
          >
            {chapters.map((ch) => {
              const Icon = CHAPTER_ICONS[ch.icon] || Info;
              const isActive = ch.id === active?.id;
              return (
                <button
                  key={ch.id}
                  type="button"
                  onClick={() => setActiveId(ch.id)}
                  data-testid={`handbook-nav-${ch.id}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B5FFF]/40 ' +
                    (isActive
                      ? 'bg-[#111418] text-white'
                      : 'text-foreground/70 hover:bg-muted hover:text-foreground')
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0 truncate">{ch.label}</span>
                </button>
              );
            })}
          </nav>

          <ScrollArea ref={scrollRef} className="min-h-0 flex-1">
            <div className="mx-auto max-w-3xl px-6 py-6" data-testid="handbook-content">
              <h2 className="text-lg font-semibold tracking-tight text-foreground">
                {active?.title}
              </h2>
              {active?.intro && (
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {active.intro}
                </p>
              )}
              {active?.id === 'quickstart' && (
                <div className="mt-4">
                  <DemoLaunch t={t} onLoaded={() => onOpenChange(false)} />
                </div>
              )}
              <div className="mt-4">
                {active?.blocks.map((b, i) => renderBlock(b, i, t('beta_contact')))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
